import type { NavigationItem } from '@amp/web-app-components/src/components/Navigation/types';
import { TAB_IDs, type TabId } from '~/constants/tabs';

// Icons
import HomeIcon from '~assets/icons/home.svg';
import BrowseIcon from '~assets/icons/browse.svg';
import SearchIcon from '~assets/icons/sidebar-search.svg';

// JET Types
import {
    type FlowAction,
    type MakeFlowAction,
    type Intent,
} from '~/jet/models';

// Intent Utils
import {
    makeBrowsePageIntent,
    makeHomePageIntent,
    makeSearchPageIntent,
} from '~/jet/intents';

// Utils
import { Locale as LocaleUtil, type Locale } from '~/utils/locale';

export interface Item extends NavigationItem {
    id: TabId;
    opaqueData: MakeFlowAction;
}

export const defaultRoute = '';

export const searchItem = makeItem(
    {
        id: TAB_IDs.search,
        locKey: 'ASE.Web.MusicClassical.NavigationItems.Search.Label',
        icon: SearchIcon,
        url: '/search',
        makeIntent: makeSearchPageIntent,
    },
    makeNavigationAction,
);

// NOTE: Storefront and language are added to the URLs in components/structure/Header.svelte
export const items: Item[] = [
    ...makeItems(
        [
            {
                id: TAB_IDs.home,
                locKey: 'ASE.Web.MusicClassical.NavigationItems.Home.Label',
                icon: HomeIcon,
                targetId: 'home',
                url: '',
                makeIntent: makeHomePageIntent,
            },
            {
                id: TAB_IDs.browse,
                locKey: 'ASE.Web.MusicClassical.NavigationItems.Browse.Label',
                icon: BrowseIcon,
                url: '/browse',
                makeIntent: makeBrowsePageIntent,
            },
        ],
        makeNavigationAction,
    ),
    searchItem,
];

interface ItemProps {
    id: TabId;
    locKey: string;
    icon: SVGElement;
    url: string;
    targetId?: string;
    makeIntent: (locale: Locale) => Intent<FlowAction>;
}

interface MakeOpaqueDataFn {
    (
        intent: Intent<FlowAction>,
        targetId: string,
        actionUrl: string,
    ): FlowAction;
}

export function makeItems(
    items: ItemProps[],
    makeOpaqueData: MakeOpaqueDataFn,
): Item[] {
    return items.map((item) => makeItem(item, makeOpaqueData));
}

export function makeItem(
    props: ItemProps,
    makeOpaqueData: MakeOpaqueDataFn,
): Item {
    const { id, locKey, icon, url, targetId, makeIntent } = props;

    return {
        id,
        locKey,
        icon,
        url,
        // This is a closure to allow us to pass in the proper storefront and
        // language on click
        opaqueData: (props: Locale): FlowAction => {
            const canonicalLocale = LocaleUtil.canonicalize(props);
            return makeOpaqueData(
                makeIntent(canonicalLocale),
                targetId || id.type,
                url,
            );
        },
    };
}

export function makeNavigationAction(
    intent: Intent<AnyPageModel>,
    targetId: string,
    actionUrl: string,
): FlowAction {
    return {
        $kind: 'flowAction',
        destination: intent,
        url: actionUrl,
        actionMetrics: {
            data: [
                {
                    // See: rdar://55668578 (Metrics: Click events not fired for Tab navigation)
                    fields: {
                        actionType: 'navigate',
                        eventType: 'click',
                        targetId,
                        targetType: 'tab',
                        actionUrl,
                    },
                    shouldFlush: false,
                    includingFields: ['pageFields', 'languages'],
                    excludingFields: [],
                },
            ],
            custom: {},
        },
    } as FlowAction;
}
